import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import Supercluster from 'supercluster';
import type {Map as Mapbox} from 'mapbox-gl';
import type {BBox} from 'geojson';

interface MarkerProperties {
  markerId: string;
  eventType?: string;
  startDate?: string | null;
}

/** An individual (unclustered) marker to render */
export interface IndividualRenderItem {
  type: 'marker';
  id: string;
  lat: number;
  lng: number;
  eventType: string;
}

/** A cluster of multiple markers to render */
export interface ClusterRenderItem {
  type: 'cluster';
  clusterId: number;
  lat: number;
  lng: number;
  pointCount: number;
  markerIds: string[];
}

/** A group of markers at the exact same coordinates, fanned out as pills */
export interface ColocatedRenderItem {
  type: 'colocated';
  lat: number;
  lng: number;
  markers: {id: string; eventType: string; startDate: Date | null}[];
}

export type RenderItem =
  | IndividualRenderItem
  | ClusterRenderItem
  | ColocatedRenderItem;

interface MapViewState {
  bbox: BBox;
  zoom: number;
}

interface UseClusteredMarkersResult {
  renderItems: RenderItem[];
  getClusterExpansionZoom: (clusterId: number) => number;
}

const CLUSTER_RADIUS = 15;
const CLUSTER_MAX_ZOOM = 16;

/**
 * Computes clustered marker groups from raw markers using the current map
 * viewport. Clusters recalculate automatically on zoom and pan.
 */
export interface MarkerInput {
  lat: number;
  lng: number;
  id: string;
  eventType?: string;
  startDate?: Date | null;
}

export function useClusteredMarkers(
  markers: MarkerInput[],
  map: Mapbox | null
): UseClusteredMarkersResult {
  const [viewState, setViewState] = useState<MapViewState | null>(null);

  const index = useMemo(() => {
    const sc = new Supercluster<MarkerProperties>({
      radius: CLUSTER_RADIUS,
      maxZoom: CLUSTER_MAX_ZOOM,
      minPoints: 2,
    });

    const features: Supercluster.PointFeature<MarkerProperties>[] = markers.map(
      (m) => ({
        type: 'Feature',
        geometry: {type: 'Point', coordinates: [m.lng, m.lat]},
        properties: {
          markerId: m.id,
          eventType: m.eventType,
          startDate: m.startDate ? m.startDate.toISOString() : null,
        },
      })
    );

    sc.load(features);
    return sc;
  }, [markers]);

  const indexRef = useRef(index);
  indexRef.current = index;

  useEffect(() => {
    if (!map) return;

    const updateViewState = () => {
      const bounds = map.getBounds();
      if (!bounds) return;

      const sw = bounds.getSouthWest();
      const ne = bounds.getNorthEast();
      setViewState({
        bbox: [sw.lng, sw.lat, ne.lng, ne.lat],
        zoom: Math.floor(map.getZoom()),
      });
    };

    // Set initial state once map is ready
    if (map.isStyleLoaded()) {
      updateViewState();
    } else {
      map.once('load', updateViewState);
    }

    map.on('zoom', updateViewState);
    map.on('moveend', updateViewState);

    return () => {
      map.off('zoom', updateViewState);
      map.off('moveend', updateViewState);
    };
  }, [map]);

  const prevRenderItemsRef = useRef<RenderItem[]>([]);

  const renderItems = useMemo((): RenderItem[] => {
    if (!viewState) return prevRenderItemsRef.current;

    const clusters = index.getClusters(viewState.bbox, viewState.zoom);

    const clusterItems: RenderItem[] = [];
    const individualItems: {
      id: string;
      lat: number;
      lng: number;
      eventType?: string;
      startDate?: string | null;
    }[] = [];

    for (const feature of clusters) {
      const [lng, lat] = feature.geometry.coordinates;

      if ('cluster' in feature.properties && feature.properties.cluster) {
        const clusterProps = feature.properties;
        const leaves = index.getLeaves(clusterProps.cluster_id, Infinity);
        const markerIds = leaves.map((leaf) => leaf.properties.markerId);

        clusterItems.push({
          type: 'cluster',
          clusterId: clusterProps.cluster_id,
          lat,
          lng,
          pointCount: clusterProps.point_count,
          markerIds,
        });
      } else {
        individualItems.push({
          id: feature.properties.markerId,
          lat,
          lng,
          eventType: feature.properties.eventType,
          startDate: feature.properties.startDate,
        });
      }
    }

    // Group individual markers by exact coordinates to detect co-located events
    const locationGroups = new Map<string, typeof individualItems>();
    for (const item of individualItems) {
      const key = `${item.lat},${item.lng}`;
      const group = locationGroups.get(key);
      if (group) {
        group.push(item);
      } else {
        locationGroups.set(key, [item]);
      }
    }

    const next: RenderItem[] = [...clusterItems];
    for (const group of locationGroups.values()) {
      if (group.length > 1) {
        next.push({
          type: 'colocated',
          lat: group[0].lat,
          lng: group[0].lng,
          markers: group.map((m) => ({
            id: m.id,
            eventType: m.eventType ?? 'ride',
            startDate: m.startDate ? new Date(m.startDate) : null,
          })),
        });
      } else {
        next.push({
          type: 'marker',
          id: group[0].id,
          lat: group[0].lat,
          lng: group[0].lng,
          eventType: group[0].eventType ?? 'ride',
        });
      }
    }

    // Avoid producing a new reference when the content hasn't changed,
    // which prevents the parent from re-creating DOM markers unnecessarily.
    const prev = prevRenderItemsRef.current;
    if (
      prev.length === next.length &&
      JSON.stringify(prev) === JSON.stringify(next)
    ) {
      return prev;
    }

    prevRenderItemsRef.current = next;
    return next;
  }, [index, viewState]);

  const getClusterExpansionZoom = useCallback(
    (clusterId: number) => indexRef.current.getClusterExpansionZoom(clusterId),
    []
  );

  return {renderItems, getClusterExpansionZoom};
}
